/* eslint-disable react/jsx-props-no-spreading */
import React, { useEffect } from 'react';
import {
  Box,
  Stack,
  Grid,
  TextField,
  Button,
  Alert,
  AlertTitle,
  Typography,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Card,
  CardContent,
  IconButton,
  MenuItem,
  Tooltip,
  tooltipClasses,
  Avatar,
} from '@mui/material';
import {
  LoadingButton,
} from '@mui/lab';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import {
  Controller,
} from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import {
  useForm,
  getFormProps,
  MultiCountrySelector,
  OutlinedIcons,
  getFieldProps,
  Link,
  ZAvatar,
} from '@marageti/z4-lib';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { PartnerCountry, TripMatch } from '@marageti/z4-sdk/lib/travel';
import { Traveler } from '@marageti/z4-sdk/lib/people';
import { CurrencyAmount } from '@marageti/z4-sdk/lib/standards';
import { addYears } from 'date-fns';
import CurrencyInput from '../../currency-input';
import ReadOnlyCurrencySelect from '../../read-only-currency-select';
import config from '../../../config';
import CommissionInfo from '../../commission-info';
import { isCanceledSale } from '../../../utils/sales';

const tooltipProps = {
  modifiers: [
    {
      name: 'offset',
      options: {
        offset: [0, -12],
      },
    },
  ],
  sx: {
    [`& .${tooltipClasses.tooltip}`]: {
      maxWidth: {
        xs: '275px',
        md: 'none',
      },
    },
  },
};

type FormDefaultValues = {
  countries: string[] | undefined,
  departureDate: Date | undefined,
  totalNights: number | undefined,
  numberOfAdults: number | undefined,
  numberOfChildren: number | undefined,
  price: Partial<CurrencyAmount> | undefined,
  commission: Partial<CurrencyAmount> | undefined,
  priceComments: string | undefined,
  document?: any[] | undefined,
};

const DEFAULT_VALUES = {
  countries: [],
  departureDate: undefined,
  totalNights: undefined,
  numberOfAdults: undefined,
  numberOfChildren: undefined,
  price: {
    amount: 0,
    currencyCode: 'USD',
  },
  commission: {
    amount: 0,
    currencyCode: 'USD',
  },
  priceComments: '',
} as FormDefaultValues;

const schema = z.object({
  countries: z.array(z.string()).min(1, { message: 'Please add at least one country' }),
  departureDate: z.date({ required_error: 'Enter the departure Date' }),
  totalNights: z.coerce.number().min(1, 'Enter the total number of nights'),
  numberOfAdults: z.coerce.number().min(1, 'Enter the number of adults'),
  numberOfChildren: z.coerce.number().min(0, 'Enter the number of children'),
  price: z.object({
    amount: z.number().min(0, 'Total Trip Price must be a positive value').max(9999999, 'Total Trip Price cannot exceed 9,999,999'),
    currencyCode: z.string(),
  }),
  commission: z.object({
    amount: z.number().min(0, 'Total Trip Commission must be a positive value').max(9999999, 'Total Trip Commission cannot exceed 9,999,999'),
    currencyCode: z.string(),
  }),
  priceComments: z.string().min(1, { message: 'Required' }),
  document: z
    .any()
    // Optional but if provided, must be a file with size <= 50 MB
    .refine((files) => files === undefined || files.length === 0 || files[0]?.size <= 50 * 1024 * 1024, 'Max file size is 50 MB')
    // Only PDF, TXT, DOC, DOCX
    .refine(
      (files) => files === undefined || files.length === 0 || [
        'application/pdf',
        'text/plain',
        'application/msword',
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      ].includes(files?.[0]?.type),
      'Only .pdf, .txt, .doc, .docx files are allowed',
    ),
}).refine((data) => data.commission.amount < data.price.amount, {
  message: 'Commission must be lower than the total sale amount.',
  path: ['commission', 'amount'],
});

type FormProps = {
  tripMatch: TripMatch,
  countries: PartnerCountry[] | undefined,
  defaultValues: {
    countries: string[] | undefined,
    departureDate: Date | undefined,
    totalNights: number | undefined,
    numberOfAdults: number | undefined,
    numberOfChildren: number | undefined,
    price: Partial<CurrencyAmount> | undefined,
    commission: Partial<CurrencyAmount> | undefined,
    priceComments: string | undefined,
    document?: any[] | undefined,
  },
  onCancel?: () => void
  onSubmit: (data: any) => void,
  primaryTraveler: Traveler | undefined,
  submissionError: boolean
};

const Form = ({
  tripMatch,
  primaryTraveler,
  countries,
  defaultValues,
  onSubmit,
  submissionError,
  onCancel = undefined,
}: FormProps) => {
  const {
    register,
    handleSubmit,
    formState: { isSubmitting, errors },
    control,
    watch,
    setValue,
    trigger,
  } = useForm({
    resolver: zodResolver(schema),
    defaultValues: {
      ...DEFAULT_VALUES,
      ...defaultValues,
    },
  });

  const selectedDocument = watch('document');
  const hasDocument = selectedDocument && selectedDocument.length > 0;
  const currency = watch('price.currencyCode');
  const totalTripPrice = watch('price.amount');
  const maxDate = addYears(new Date(), 10);
  // On canceled trips the trip details can't be updated
  // so we hide that section and keep only the sales fields editable.
  const hideTripDetails = isCanceledSale(tripMatch.status);

  useEffect(() => {
    if (!isSubmitting) return undefined;
    const handler = (e: BeforeUnloadEvent) => { e.preventDefault(); };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [isSubmitting]);

  useEffect(() => {
    setValue('commission.currencyCode', currency);
  }, [currency]);

  useEffect(() => {
    trigger('document');
  }, [selectedDocument]);

  const handleRemoveDocument = () => {
    setValue('document', undefined);
  };

  return (
    <form {...getFormProps(handleSubmit(onSubmit))}>
      <Stack spacing={2} useFlexGap>
        {!hideTripDetails && (
        <>
          <div>
            <Typography
              variant="h3"
              sx={{
                mb: 1,
              }}
            >
              Confirm Trip Details
            </Typography>
            <Typography color="text.secondary">
              Please confirm the sold trip details below. These will be shown to the traveler.
            </Typography>
          </div>

          {primaryTraveler && (
            <Card variant="dense" sx={{ mt: 3 }}>
              <CardContent>
                <List disablePadding>
                  <ListItem dense disableGutters>
                    <ListItemAvatar>
                      <ZAvatar size={32} src={primaryTraveler?.avatar} name={primaryTraveler?.fullName} />
                    </ListItemAvatar>
                    <ListItemText
                      primary={primaryTraveler.fullName}
                      primaryTypographyProps={{
                        variant: 'bodyBold',
                      }}
                      secondary="Primary Traveler"
                    />
                  </ListItem>
                </List>
              </CardContent>
            </Card>
          )}

          <Grid
            container
            columnSpacing={2}
          >
            <Grid item mobile={12} desktop={12}>
              <Controller
                name="countries"
                control={control}
                render={({ field }) => (
                  <MultiCountrySelector
                    {...field}
                    {...getFieldProps('countries', errors)}
                    label="Countries"
                    countries={countries}
                    required
                    onChange={(_: any, data: String[]) => {
                      field.onChange(data);
                    }}
                  />
                )}
              />
            </Grid>

            <Grid item mobile={12} desktop={6}>
              <Controller
                name="departureDate"
                control={control}
                render={({
                  field: {
                    onChange, onBlur, value, ref, name,
                  },
                }) => (
                  <DatePicker
                    label="Departure Date (MM/DD/YYYY)"
                    onChange={onChange}
                    value={value}
                    maxDate={maxDate}
                    slotProps={{
                      textField: {
                        name,
                        onBlur,
                        fullWidth: true,
                        ...getFieldProps('departureDate', errors),
                      },
                    }}
                    inputRef={ref}
                  />
                )}
              />
            </Grid>

            <Grid item mobile={12} desktop={6}>
              <TextField
                type="number"
                fullWidth
                label="Nights"
                required
                {...register('totalNights')}
                {...getFieldProps('totalNights', errors)}
              />
            </Grid>

            <Grid item mobile={12} desktop={6}>
              <TextField
                type="number"
                fullWidth
                label="Adults"
                required
                {...register('numberOfAdults')}
                {...getFieldProps('numberOfAdults', errors)}
              />
            </Grid>

            <Grid item mobile={12} desktop={6}>
              <TextField
                type="number"
                fullWidth
                label="Children"
                {...register('numberOfChildren')}
                {...getFieldProps('numberOfChildren', errors)}
              />
            </Grid>
          </Grid>

          <Divider
            sx={{
              my: 2,
            }}
          />
        </>
        )}

        {/* Total Trip Price */}
        <div>
          <Stack spacing={1} mb={2}>
            <Stack direction="row" spacing={1} alignItems="center">
              <Typography
                variant="h3"
              >
                Confirm Trip Price
              </Typography>
              {currency !== 'USD' && (
                <Tooltip
                  placement="top-start"
                  enterTouchDelay={100}
                  PopperProps={tooltipProps}
                  title="Please note that the traveler will see an approximate conversion to USD."
                >
                  <InfoOutlinedIcon sx={{ color: 'text.secondary' }} />
                </Tooltip>
              )}
            </Stack>
            <Typography color="text.secondary">Please note that the total trip price entered will be shown to the traveler.</Typography>
          </Stack>
          <Grid container columnSpacing={2}>
            <Grid item mobile={4} desktop={2}>
              <TextField
                fullWidth
                label="Currency"
                select
                {...register('price.currencyCode')}
                {...getFieldProps('price.currencyCode', errors)}
                InputProps={{
                  defaultValue: defaultValues?.price?.currencyCode,
                }}
              >
                <MenuItem key="USD" value="USD">USD</MenuItem>
                <MenuItem key="EUR" value="EUR">EUR</MenuItem>
                <MenuItem key="GBP" value="GBP">GBP</MenuItem>
              </TextField>
            </Grid>

            <Grid item mobile={8} desktop={10}>
              <Controller
                control={control}
                name="price.amount"
                render={({ field: { onChange, value, ...field } }) => (
                  <CurrencyInput
                    label="Amount"
                    fullWidth
                    required
                    error={!!errors.price?.amount}
                    helperText={errors.price?.amount?.message}
                    defaultValue={defaultValues?.price?.amount}
                    numericFormatProps={{
                      allowNegative: false,
                      currencyCode: currency,
                    }}
                    {...field}
                    onChange={(newValue) => {
                      onChange(newValue);
                    }}
                  />
                )}
              />
            </Grid>
          </Grid>
        </div>

        <Divider
          sx={{
            my: 2,
          }}
        />

        {/* Commission to Zicasso */}
        <div>
          <Stack spacing={1} mb={2}>
            <Typography
              variant="h3"
            >
              Commission to Zicasso
            </Typography>
            <Typography color="text.secondary">
              These details will&nbsp;
              <Typography component="span" variant="bodyBold">not be shown</Typography>
              &nbsp;to the traveler.
            </Typography>
          </Stack>
          <Grid container columnSpacing={2} mb={3}>
            <Grid item mobile={4} desktop={2}>
              <Controller
                control={control}
                name="commission.currencyCode"
                defaultValue={defaultValues?.commission?.currencyCode}
                render={({ field }) => (
                  <ReadOnlyCurrencySelect {...field} />
                )}
              />
            </Grid>

            <Grid item mobile={8} desktop={10}>
              <Controller
                control={control}
                name="commission.amount"
                render={({ field: { onChange, value, ...field } }) => (
                  <CurrencyInput
                    label="Amount"
                    fullWidth
                    required
                    error={!!errors.commission?.amount}
                    helperText={errors.commission?.amount?.message}
                    defaultValue={defaultValues?.commission?.amount}
                    numericFormatProps={{
                      allowNegative: false,
                      currencyCode: currency,
                    }}
                    {...field}
                    onChange={(newValue) => {
                      onChange(newValue);
                    }}
                  />
                )}
              />
            </Grid>

            <Grid item mobile={12} desktop={12}>
              <CommissionInfo tripMatch={tripMatch} tripPrice={{ amount: totalTripPrice ?? 0, currencyCode: currency }} />
            </Grid>
          </Grid>

          <Stack spacing={1} useFlexGap>
            <TextField
              label="Comments"
              multiline
              minRows={6}
              required
              {...register('priceComments')}
              {...getFieldProps('priceComments', errors)}
            />
            <Typography variant="caption">
              Describe how you calculated the commission. Please see&nbsp;
              <Link sx={{ textDecoration: 'underline' }} to={`${config.Z3_URL}tc-terms-agreement`}>Travel Company Agreement</Link>
              &nbsp;(section 5) for specific details.
            </Typography>
          </Stack>
        </div>

        <Divider
          sx={{
            my: 2,
          }}
        />

        <Box
          sx={{
            mb: 2,
          }}
        >
          <Typography
            variant="h3"
            sx={{
              mb: 1,
            }}
          >
            Confirmed Itinerary / Quote (For
            {' '}
            Zicasso&apos;s Records)
          </Typography>
          <Typography color="text.secondary">
            Please submit an itinerary as proof of the services provided in connection with this sales recording.
            Files must not exceed 50 MB and can be the following file formats: txt, pdf, doc, and docx.
            {' '}
            <Typography component="span" variant="bodyBold">
              This file is NOT shared with the traveler.
            </Typography>
          </Typography>

          <Button
            component="label"
            variant="outlined"
            size="small"
            sx={{
              my: 2,
              display: hasDocument ? 'none' : 'inline-flex',
            }}
          >
            Browse...
            <input
              type="file"
              accept=".pdf,.txt,.doc,.docx"
              hidden
              {...register('document')}
            />
          </Button>

          {hasDocument && (
            <>
              <List
                disablePadding
              >
                <ListItem
                  disableGutters
                  secondaryAction={(
                    <IconButton
                      edge="end"
                      aria-label="remove document"
                      onClick={handleRemoveDocument}
                    >
                      <OutlinedIcons.OutlinedTrashIcon color="primary" />
                    </IconButton>
                  )}
                >
                  <ListItemAvatar>
                    <Avatar variant="rounded" sx={{ width: 32, height: 32 }}>
                      <OutlinedIcons.OutlinedDocumentIcon />
                    </Avatar>
                  </ListItemAvatar>
                  <ListItemText
                    primary={selectedDocument[0].name}
                  />
                </ListItem>
              </List>
              {errors.document && (
                <Typography variant="caption" color="error">{errors.document.message}</Typography>
              )}
            </>
          )}
        </Box>

        {submissionError && (
          <Alert severity="error">
            <AlertTitle>There was an error submitting the form.</AlertTitle>
            Please try again.
          </Alert>
        )}

        <Stack
          direction="row"
          spacing={3}
          sx={(theme) => ({
            [theme.breakpoints.down('desktop')]: {
              marginTop: 'auto',
              flexDirection: 'column-reverse',
              '& > :not(:first-of-type)': {
                margin: theme.spacing(0, 0, 2.5, 0),
              },
            },
          })}
        >
          <LoadingButton
            variant="contained"
            type="submit"
            loading={isSubmitting}
            disabled={isSubmitting}
          >
            Save
          </LoadingButton>

          {onCancel && (
            <Button variant="outlined" onClick={onCancel}>
              Cancel
            </Button>
          )}
        </Stack>
      </Stack>
    </form>
  );
};

export default Form;
